import React, { useRef, useCallback, useEffect } from 'react';
import { XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import { TextField, Textarea } from '@/shared/components/TextField';
import { Button } from '@/shared/components/Buttons';
import { Tooltip } from '@/shared/components/Tooltip';
import CreateLayout from '@/shared/layout/createLayout';
import { TASK_FORM_LABELS } from '../../constants/labels';
import { useCreateForm, type FormErrors } from '../../hooks/useCreateForm';
import type { FormBuilderState, FormSection } from '../../types';
import SectionEditor from './SectionEditor';
import FormPreview from './FormPreview';

// ─── Alert banner ─────────────────────────────────────────────────────────────

const AlertBanner: React.FC<{ text: string }> = ({ text }) => {
  const [dismissed, setDismissed] = React.useState(false);
  if (dismissed) {
    return null;
  }
  return (
    <div className="flex items-center gap-3 bg-[#EAF3FF] rounded-lg px-4 py-3">
      <InformationCircleIcon className="w-6 h-6 text-[#1E7CE8] shrink-0" />
      <p className="text-sm leading-5 tracking-[0.25px] text-[#1E7CE8] flex-1">
        <strong className="font-medium">Uwaga:</strong> {text}
      </p>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="text-[#1E7CE8] hover:text-[#155db5] shrink-0 cursor-pointer"
        aria-label="Zamknij"
      >
        <XMarkIcon width={24} height={24} />
      </button>
    </div>
  );
};

// ─── Section divider ──────────────────────────────────────────────────────────

const SectionDivider = () => <hr className="border-[#DCDDE0]" />;

// ─── FormBuilder ─────────────────────────────────────────────────────────────

export interface FormBuilderProps {
  formState: FormBuilderState;
  errors: FormErrors;
  isSubmitting: boolean;
  setName: (name: string) => void;
  setDescription: (description: string) => void;
  addSection: () => void;
  updateSection: (idx: number, updated: FormSection) => void;
  removeSection: (idx: number) => void;
  reorderSections: (from: number, to: number) => void;
  sectionDragIndexRef: React.MutableRefObject<number | null>;
  formId?: string;
}

export const FormBuilder: React.FC<FormBuilderProps> = ({
  formState,
  errors,
  isSubmitting,
  setName,
  setDescription,
  addSection,
  updateSection,
  removeSection,
  reorderSections,
  sectionDragIndexRef,
  formId,
}) => (
  <>
    {/* Info alert */}
    <AlertBanner text={TASK_FORM_LABELS.ALERT_TEXT} />

    {/* ── Form details ── */}
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-titleDefault text-[#474A4C]">
          {TASK_FORM_LABELS.SECTION_FORM_DETAILS}
        </h2>
        <p className="text-sm text-[#474A4C] mt-0.5">
          {TASK_FORM_LABELS.FORM_DETAILS_DESC}
        </p>
      </div>

      {formId !== undefined ? (
        <div className="flex gap-4 w-[59%]">
          <div
            className="flex-1"
            data-error-field={errors.name ? 'true' : undefined}
          >
            <TextField
              label={TASK_FORM_LABELS.FIELD_FORM_NAME}
              value={formState.name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              autoComplete="off"
              error={Boolean(errors.name)}
              errorMessage={errors.name ?? ''}
              disabled={isSubmitting}
            />
          </div>
          <div className="flex-1">
            <TextField
              label={TASK_FORM_LABELS.VIEW_LABEL_FORM_ID}
              value={formId}
              disabled
              fullWidth
            />
          </div>
        </div>
      ) : (
        <div data-error-field={errors.name ? 'true' : undefined}>
          <TextField
            label={TASK_FORM_LABELS.FIELD_FORM_NAME}
            value={formState.name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            width="50%"
            autoFocus
            autoComplete="off"
            error={Boolean(errors.name)}
            errorMessage={errors.name ?? ''}
            disabled={isSubmitting}
          />
        </div>
      )}

      <div data-error-field={errors.description ? 'true' : undefined}>
        <Textarea
          label={TASK_FORM_LABELS.FIELD_FORM_DESCRIPTION}
          value={formState.description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={500}
          fullWidth
          rows={4}
          disabled={isSubmitting}
          error={Boolean(errors.description)}
          errorMessage={errors.description ?? ''}
        />
      </div>
    </div>

    <SectionDivider />

    {/* ── Sections ── */}
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-base font-normal text-[#474A4C]">
          {TASK_FORM_LABELS.SECTION_SECTIONS} ({formState.sections.length})
        </h2>
        <p className="text-sm text-[#474A4C] mt-0.5">
          {TASK_FORM_LABELS.SECTIONS_DESC}
        </p>
      </div>

      {errors.general && (
        <p className="text-sm text-red-500">{errors.general}</p>
      )}

      <div className="flex flex-col gap-4">
        {formState.sections.map((section, idx) => (
          <SectionEditor
            key={section.id}
            section={section}
            sectionIndex={idx}
            onChange={(updated) => updateSection(idx, updated)}
            onRemove={() => removeSection(idx)}
            isOnly={formState.sections.length === 1}
            isFirst={idx === 0}
            errors={errors.sections?.[section.id]}
            onDragStart={() => {
              sectionDragIndexRef.current = idx;
            }}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => {
              const from = sectionDragIndexRef.current;
              if (from !== null && from !== idx) {
                reorderSections(from, idx);
              }
              sectionDragIndexRef.current = null;
            }}
          />
        ))}
      </div>

      {formState.sections.length >= 10 ? (
        <Tooltip
          content={TASK_FORM_LABELS.TOOLTIP_MAX_SECTIONS}
          side="top"
          align="center"
          forceShow
          triggerTabIndex={-1}
          cursor="not-allowed"
        >
          <Button
            type="button"
            label={TASK_FORM_LABELS.BUTTON_ADD_SECTION}
            buttonType="Square"
            size="Small"
            onClick={addSection}
            disabled
            customBorderColor="#DCDDE0"
            customBgColor="transparent"
            customTextColor="#474A4C"
            customFontWeight={500}
            icon={<PlusIcon className="w-5 h-5" />}
            showIcon
            className="w-full hover:!bg-[#F0F0F0]"
          />
        </Tooltip>
      ) : (
        <Button
          type="button"
          label={TASK_FORM_LABELS.BUTTON_ADD_SECTION}
          buttonType="Square"
          size="Small"
          onClick={addSection}
          customBorderColor="#DCDDE0"
          customBgColor="transparent"
          customTextColor="#474A4C"
          customFontWeight={500}
          icon={<PlusIcon className="w-5 h-5" />}
          showIcon
          className="w-full hover:!bg-[#F0F0F0]"
        />
      )}
    </div>

    <SectionDivider />

    {/* ── Signature ── */}
    <div className="flex flex-col gap-3 pb-2">
      <div>
        <h2 className="text-titleDefault text-[#474A4C]">
          {TASK_FORM_LABELS.SIGNATURE_SECTION}
        </h2>
        <p className="text-sm text-[#474A4C] mt-0.5">
          {TASK_FORM_LABELS.SIGNATURE_DESC}
        </p>
      </div>

      <label className="flex items-center gap-2 cursor-not-allowed select-none">
        <input
          type="checkbox"
          checked
          disabled
          className="w-4 h-4 accent-[#2A4455] cursor-not-allowed"
        />
        <span className="text-sm text-neutral-10">
          {TASK_FORM_LABELS.SIGNATURE_REQUIRED}
        </span>
      </label>
    </div>
  </>
);

// ─── Page header ──────────────────────────────────────────────────────────────

const FormPageHeader = () => (
  <p className="text-titleLarge text-neutral-10">
    {TASK_FORM_LABELS.CREATE_TITLE}
  </p>
);

// ─── CreateForm ───────────────────────────────────────────────────────────────

const CreateForm: React.FC = () => {
  const {
    formState,
    errors,
    isSubmitting,
    isCancelModalOpen,
    isErrorSnackbarOpen,
    snackbarError,
    isPreviewOpen,
    validationAttemptCount,
    setName,
    setDescription,
    addSection,
    updateSection,
    removeSection,
    reorderSections,
    handleSaveFromPreview,
    handleCancel,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleOpenPreview,
    handleClosePreview,
    handleCloseErrorSnackbar,
  } = useCreateForm();

  const sectionDragIndexRef = useRef<number | null>(null);

  useEffect(() => {
    if (validationAttemptCount === 0) {
      return;
    }
    requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>('[data-error-field="true"]')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [validationAttemptCount]);

  const handlePreviewSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      handleOpenPreview();
    },
    [handleOpenPreview]
  );

  const handleSaveSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      void handleSaveFromPreview();
    },
    [handleSaveFromPreview]
  );

  if (isPreviewOpen) {
    return (
      <CreateLayout
        key="preview"
        isSubmitting={isSubmitting}
        handleSubmit={handleSaveSubmit}
        handleCancel={handleClosePreview}
        isCancelModalOpen={false}
        handleCloseCancelModal={() => {}}
        handleConfirmCancel={() => {}}
        isSnackbarOpen={isErrorSnackbarOpen}
        handleCloseSnackbar={handleCloseErrorSnackbar}
        snackbarError={snackbarError}
        cancelButtonLabel={TASK_FORM_LABELS.BUTTON_BACK_TO_EDIT}
        submitButtonLabel={TASK_FORM_LABELS.BUTTON_SAVE}
        submitButtonLoadingLabel={TASK_FORM_LABELS.BUTTON_SAVING}
        Header={FormPageHeader}
        headerClassName="mb-5 py-2"
      >
        <FormPreview formState={formState} isSubmitting={isSubmitting} />
      </CreateLayout>
    );
  }

  return (
    <CreateLayout
      key="edit"
      isSubmitting={false}
      handleSubmit={handlePreviewSubmit}
      handleCancel={handleCancel}
      isCancelModalOpen={isCancelModalOpen}
      handleCloseCancelModal={handleCloseCancelModal}
      handleConfirmCancel={handleConfirmCancel}
      isSnackbarOpen={isErrorSnackbarOpen}
      handleCloseSnackbar={handleCloseErrorSnackbar}
      snackbarError={snackbarError}
      cancelButtonLabel={TASK_FORM_LABELS.BUTTON_CANCEL}
      submitButtonLabel={TASK_FORM_LABELS.BUTTON_PREVIEW}
      Header={FormPageHeader}
      headerClassName="mb-5 py-2"
    >
      <FormBuilder
        formState={formState}
        errors={errors}
        isSubmitting={isSubmitting}
        setName={setName}
        setDescription={setDescription}
        addSection={addSection}
        updateSection={updateSection}
        removeSection={removeSection}
        reorderSections={reorderSections}
        sectionDragIndexRef={sectionDragIndexRef}
      />
    </CreateLayout>
  );
};

export default CreateForm;
